import React, { useCallback, useEffect, useRef, useState } from "react";
import Icons from "../icons";

/* Epic dropdown.

   A native <select> popup always sizes to its widest option and cannot wrap
   text, so long epic titles blow the control's width out. This replacement
   keeps a fixed control/panel width and wraps long titles onto multiple lines.
   Closes on outside-click or Escape.

   Shared by the Tickets page and the Explore & Author setup. */
export default function EpicSelect({ value, onChange, epics = [], loading = false, className = "" }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const selected = epics.find((e) => e.key === value) || null;
  const disabled = loading || !epics.length;

  // Panel is max-h-72 (288px). Flip up when there isn't room below.
  const PANEL_H = 300;

  const placePanel = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    setDropUp(below < PANEL_H && above > below);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    placePanel();
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", placePanel);
    window.addEventListener("scroll", placePanel, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", placePanel);
      window.removeEventListener("scroll", placePanel, true);
    };
  }, [open, placePanel]);

  const label = loading
    ? "Loading epics…"
    : selected
    ? `${selected.key} — ${selected.summary}`
    : epics.length
    ? "Select an epic…"
    : "No epics in this project";

  return (
    <div className={`relative ${className || "w-72"}`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title={label}
        ref={btnRef}
        className="flex h-[38px] w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-card px-3 text-left text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
      >
        <span className={`min-w-0 flex-1 truncate ${selected ? "" : "text-slate-400"}`}>
          {label}
        </span>
        <Icons.ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
      </button>
      {open && !disabled && (
        <div
          className={[
            "absolute left-0 z-30 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-card py-1 shadow-lg",
            dropUp ? "bottom-full mb-1" : "top-full mt-1",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            className="block w-full px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
          >
            Select an epic…
          </button>
          {epics.map((ep) => (
            <button
              key={ep.key}
              type="button"
              onClick={() => {
                onChange(ep.key);
                setOpen(false);
              }}
              className={`block w-full whitespace-normal break-words px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                ep.key === value ? "bg-brand-50 text-brand-800" : "text-slate-700"
              }`}
            >
              <span className="font-mono font-semibold">{ep.key}</span> — {ep.summary}
              {/* Provenance, when the caller knows it: which document this epic
                  was built from. Optional, so pickers that don't track sources
                  (the Tickets page) are unaffected. */}
              {ep.sourceLabel && (
                <span className="mt-1 flex items-center gap-1 text-[11px] text-emerald-700">
                  <Icons.Report className="h-3 w-3 flex-none" />
                  <span className="min-w-0 truncate">{ep.sourceLabel}</span>
                  {ep.sourceCount > 1 && (
                    <span className="flex-none text-emerald-600/70">+{ep.sourceCount - 1}</span>
                  )}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
